import type {
  MouldingGraphNodeData,
  MouldingNodeRunResult,
  MouldingStepParameters,
} from '../../../models/moulding-item.model';

/**
 * Single source of truth for what node kinds exist on the Moulding board.
 * `MouldingGraphService` stays generic and delegates to the matching
 * `NodeDefinition` (via `getNodeDefinition()`) for anything kind-specific.
 * To add a node kind: extend `NodeKind` + `NodeRuntimeDataByKind`, add a
 * registry entry, and create a matching `nodes/*-config-dialog` component.
 */

/** Identifiers for every node kind on the board. */
export type NodeKind =
  | 'cad_input'
  | 'pull_direction'
  | 'split_part'
  | 'fill_holes_and_cutouts'
  | 'add_runoff'
  | 'scale_cad'
  | 'create_mold_box'
  | 'create_pattern'
  | 'drill_lt_holes'
  | 'add_structure'
  | 'dfm_check';

export const PROCESSING_NODE_KINDS = [
  'split_part',
  'fill_holes_and_cutouts',
  'add_runoff',
  'scale_cad',
  'create_mold_box',
  'create_pattern',
  'drill_lt_holes',
  'add_structure',
  'dfm_check',
] as const satisfies readonly NodeKind[];

export type ProcessingNodeKind = (typeof PROCESSING_NODE_KINDS)[number];

export const ASYNC_NODE_KINDS = [
  'pull_direction',
  ...PROCESSING_NODE_KINDS,
] as const satisfies readonly NodeKind[];

export type AsyncNodeKind = (typeof ASYNC_NODE_KINDS)[number];
export const NODE_RUN_KINDS = [
  'cad_input',
  ...ASYNC_NODE_KINDS,
] as const satisfies readonly NodeKind[];
export type NodeRunKind = (typeof NODE_RUN_KINDS)[number];

/** Current persisted execution state for a node run. */
export type NodeRunStatus =
  'idle' | 'pending' | 'processing' | 'complete' | 'error';

type NodeRunRuntimeData = {
  result: MouldingNodeRunResult | null;
  status: NodeRunStatus;
  isOutdated: boolean;
  errorMessage: string | null;
};

type ProcessingNodeRuntimeData = NodeRunRuntimeData & {
  parameters: MouldingStepParameters;
};

/**
 * In-memory data per node kind, held in `MouldingGraphService.nodeMeta`
 * (vs. `MouldingGraphNodeData`, the persisted graph shape). Processing
 * results are runtime-only here: `MouldingNodeRun` is their authoritative,
 * persisted source. Read and written by each kind's `*-config-dialog`
 * component via `getNodeData()` / `updateNodeData()`.
 */
export type NodeRuntimeDataByKind = {
  cad_input: NodeRunRuntimeData;
  pull_direction: NodeRunRuntimeData;
  dfm_check: ProcessingNodeRuntimeData;
  split_part: ProcessingNodeRuntimeData;
  fill_holes_and_cutouts: ProcessingNodeRuntimeData;
  add_runoff: ProcessingNodeRuntimeData;
  scale_cad: ProcessingNodeRuntimeData;
  create_mold_box: ProcessingNodeRuntimeData;
  create_pattern: ProcessingNodeRuntimeData;
  drill_lt_holes: ProcessingNodeRuntimeData;
  add_structure: ProcessingNodeRuntimeData;
};

/**
 * Discriminated union of `{ kind, data }`, stored per-node (keyed by node
 * id) in `MouldingGraphService.nodeMeta`. Built as a mapped-type-over-union
 * so narrowing on `kind` also narrows `data`.
 */
export type NodeMeta = {
  [K in NodeKind]: { kind: K; data: NodeRuntimeDataByKind[K] };
}[NodeKind];

export type NodePortType = 'cad';

/** One input socket declared by a `NodeDefinition`. */
type NodeInputDefinition = {
  key: string;
  label: string;
  type: NodePortType;
};

/** One output socket declared by a node definition. */
type NodeOutputDefinition = {
  key: string;
  label: string;
  type: NodePortType;
};

/** Everything needed to create, (de)serialize, and display one node kind. */
type NodeDefinition<K extends NodeKind> = {
  kind: K;
  /** Node title, and label for its "add node" toolbar button (`getNodeLabel()`). */
  label: string;
  /** PrimeIcons class for its "add node" toolbar button (`getNodeIcon()`). */
  icon: string;
  inputs: NodeInputDefinition[];
  outputs: NodeOutputDefinition[];
  /** Runtime data for a brand-new node, used when the user adds one from the toolbar. */
  createInitialData: () => NodeRuntimeDataByKind[K];
  /**
   * Rebuilds this node's runtime data from its saved graph fields. Called by
   * `loadGraph()` for every node when a flow is loaded from the backend.
   */
  hydrateData: (
    serializedNode: MouldingGraphNodeData,
  ) => NodeRuntimeDataByKind[K];
  /**
   * Picks the subset of this node's runtime data that should be persisted,
   * as the fields to merge into its saved graph node. Called by
   * `serialize()` for every node whenever the flow is saved.
   */
  serializeData: (
    data: NodeRuntimeDataByKind[K],
  ) => Partial<MouldingGraphNodeData>;
  /** One-line summary shown on the node and in its config dialog. */
  describe: (data: NodeRuntimeDataByKind[K]) => string;
};

function createProcessingNodeDefinition<K extends ProcessingNodeKind>(config: {
  kind: K;
  label: string;
  icon: string;
  statusText: {
    active: string;
    complete: string;
    failed: string;
    idle: string;
  };
  additionalInputs?: NodeInputDefinition[];
  hasCadOutput?: boolean;
}): NodeDefinition<K> {
  const {
    kind,
    label,
    icon,
    statusText,
    additionalInputs = [],
    hasCadOutput = true,
  } = config;
  return {
    kind,
    label,
    icon,
    inputs: [{ key: 'cad', label: 'CAD', type: 'cad' }, ...additionalInputs],
    outputs: hasCadOutput
      ? [
          {
            key: 'cad',
            label: 'Result CAD',
            type: 'cad',
          },
        ]
      : [],
    createInitialData: () => ({
      result: null,
      parameters: {},
      status: 'idle',
      isOutdated: false,
      errorMessage: null,
    }),
    hydrateData: (serializedNode) => {
      return {
        result: null,
        parameters: serializedNode.parameters ?? {},
        status: 'idle',
        isOutdated: false,
        errorMessage: null,
      };
    },
    serializeData: (data) =>
      ({
        parameters: data.parameters,
      }) as Partial<MouldingGraphNodeData>,
    describe: (data) => {
      switch (data.status) {
        case 'pending':
        case 'processing':
          return statusText.active;
        case 'error':
          return `Error: ${data.errorMessage ?? statusText.failed}`;
        case 'complete':
          return statusText.complete;
        default:
          return statusText.idle;
      }
    },
  };
}

export const MOULDING_NODE_REGISTRY: { [K in NodeKind]: NodeDefinition<K> } = {
  cad_input: {
    kind: 'cad_input',
    label: 'CAD Input',
    icon: 'pi pi-box',
    inputs: [],
    outputs: [
      {
        key: 'cad',
        label: 'CAD',
        type: 'cad',
      },
    ],
    createInitialData: () => ({
      result: null,
      status: 'idle',
      isOutdated: false,
      errorMessage: null,
    }),
    hydrateData: () => ({
      result: null,
      status: 'idle',
      isOutdated: false,
      errorMessage: null,
    }),
    serializeData: () => ({}),
    describe: (data) => {
      switch (data.status) {
        case 'error':
          return `Error: ${data.errorMessage ?? 'Failed to save CAD input'}`;
        case 'complete':
          return 'CAD object selected';
        default:
          return 'No CAD object selected';
      }
    },
  },
  pull_direction: {
    kind: 'pull_direction',
    label: 'Pull Direction',
    icon: 'pi pi-arrow-up-right',
    inputs: [{ key: 'cad', label: 'CAD', type: 'cad' }],
    outputs: [
      {
        key: 'cad',
        label: 'CAD',
        type: 'cad',
      },
    ],
    createInitialData: () => ({
      result: null,
      status: 'idle',
      isOutdated: false,
      errorMessage: null,
    }),
    hydrateData: () => ({
      result: null,
      status: 'idle',
      isOutdated: false,
      errorMessage: null,
    }),
    serializeData: () => ({}),
    describe: (data) => {
      switch (data.status) {
        case 'pending':
        case 'processing':
          return 'Finding pull direction…';
        case 'error':
          return `Error: ${data.errorMessage ?? 'Pull direction failed'}`;
        case 'complete':
          return data.result?.artifact.pull_direction
            ? formatVector3(data.result.artifact.pull_direction)
            : 'No pull direction selected';
        default:
          return 'Not yet processed';
      }
    },
  },
  split_part: createProcessingNodeDefinition({
    kind: 'split_part',
    label: 'Split Part',
    icon: 'pi pi-clone',
    statusText: {
      active: 'Splitting…',
      complete: 'Split complete',
      failed: 'Split failed',
      idle: 'Not yet split',
    },
  }),
  fill_holes_and_cutouts: createProcessingNodeDefinition({
    kind: 'fill_holes_and_cutouts',
    label: 'Fill Holes and Cutouts',
    icon: 'pi pi-stop',
    statusText: {
      active: 'Filling holes/cutouts…',
      complete: 'Fill complete',
      failed: 'Fill failed',
      idle: 'Not yet filled',
    },
  }),
  add_runoff: createProcessingNodeDefinition({
    kind: 'add_runoff',
    label: 'Add Runoff',
    icon: 'pi pi-arrows-v',
    statusText: {
      active: 'Adding runoff…',
      complete: 'Runoff complete',
      failed: 'Runoff failed',
      idle: 'No runoff added yet',
    },
  }),
  scale_cad: createProcessingNodeDefinition({
    kind: 'scale_cad',
    label: 'Scale CAD',
    icon: 'pi pi-expand',
    statusText: {
      active: 'Scaling CAD…',
      complete: 'Scaling complete',
      failed: 'Scaling failed',
      idle: 'CAD not yet scaled',
    },
  }),
  create_mold_box: createProcessingNodeDefinition({
    kind: 'create_mold_box',
    label: 'Create Mold Box',
    icon: 'pi pi-box',
    statusText: {
      active: 'Creating mold box…',
      complete: 'Mold box complete',
      failed: 'Mold box failed',
      idle: 'No mold box created yet',
    },
  }),
  create_pattern: createProcessingNodeDefinition({
    kind: 'create_pattern',
    label: 'Create Pattern',
    icon: 'pi pi-box',
    statusText: {
      active: 'Creating pattern…',
      complete: 'Pattern complete',
      failed: 'Pattern failed',
      idle: 'No pattern created yet',
    },
  }),
  drill_lt_holes: createProcessingNodeDefinition({
    kind: 'drill_lt_holes',
    label: 'Add LT Holes',
    icon: 'pi pi-circle',
    additionalInputs: [{ key: 'pattern', label: 'Pattern CAD', type: 'cad' }],
    statusText: {
      active: 'Drilling LT holes…',
      complete: 'LT holes complete',
      failed: 'LT holes failed',
      idle: 'No LT holes added yet',
    },
  }),
  add_structure: createProcessingNodeDefinition({
    kind: 'add_structure',
    label: 'Add Backing Structure',
    icon: 'pi pi-th-large',
    statusText: {
      active: 'Adding backing structure…',
      complete: 'Backing structure complete',
      failed: 'Backing structure failed',
      idle: 'No backing structure yet',
    },
  }),
  dfm_check: createProcessingNodeDefinition({
    kind: 'dfm_check',
    label: 'DFM Check',
    icon: 'pi pi-search',
    hasCadOutput: false,
    statusText: {
      active: 'Running DFM checks…',
      complete: 'DFM checks complete',
      failed: 'DFM checks failed',
      idle: 'Select a material',
    },
  }),
};

function formatVector3(vector: { x: number; y: number; z: number }): string {
  return `(${formatNumber(vector.x)}, ${formatNumber(vector.y)}, ${formatNumber(vector.z)})`;
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return 'NaN';
  }
  const fixed = value.toFixed(6);
  return fixed.replace(/\.?0+$/, '');
}

export function getNodeDefinition<K extends NodeKind>(
  kind: K,
): NodeDefinition<K> {
  return MOULDING_NODE_REGISTRY[kind];
}

export function isProcessingNodeKind(
  kind: string | null,
): kind is ProcessingNodeKind {
  return (
    kind !== null && PROCESSING_NODE_KINDS.includes(kind as ProcessingNodeKind)
  );
}

export function isAsyncNodeKind(kind: string | null): kind is AsyncNodeKind {
  return kind !== null && ASYNC_NODE_KINDS.includes(kind as AsyncNodeKind);
}

export function isNodeRunKind(kind: string | null): kind is NodeRunKind {
  return kind !== null && NODE_RUN_KINDS.includes(kind as NodeRunKind);
}
